import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { CSS2DRenderer } from "three/addons/renderers/CSS2DRenderer.js";
import { Line2 } from "three/addons/lines/Line2.js";
import { LineMaterial } from "three/addons/lines/LineMaterial.js";
import { LineGeometry } from "three/addons/lines/LineGeometry.js";
import { centerModel, collectMeshes, disposeModel, loadModel, detectCeilingY, loadRoomPlanGlb } from "./MeshLoader.ts";
import { CameraManager } from "./Camera.ts";
import { ClipPlanes } from "./ClipPlanes.ts";
import type { ClipAxis } from "./ClipPlanes.ts";
import { FloorPlan } from "./FloorPlan.ts";
import { RoomPlanLayer } from "./RoomPlanLayer.ts";
import type { AlignmentData, RoomPlanData } from "../types.ts";
import type { IcRoomPlanJson } from "../ui/RoomPlanJson2D.ts";
import { AlignTool } from "../tools/AlignTool.ts";
import type { AlignStep } from "../tools/AlignTool.ts";
import { AnnotationManager } from "../tools/Annotation.ts";
import { NoteAnnotationManager } from "../tools/NoteAnnotation.ts";
import { MeasureTool } from "../tools/MeasureTool.ts";
import { NoteTool } from "../tools/NoteTool.ts";
import { StreetView } from "./StreetView.ts";
import { Pano360View } from "./Pano360View.ts";
import { Pano360Align } from "./Pano360Align.ts";
import type { CameraMode, MeasurementType, NoteCategory } from "../types.ts";
import type { MeasureResult } from "../tools/MeasureTool.ts";
import type { NoteResult } from "../tools/NoteTool.ts";
import { debugEnabled } from "../config.ts";

export type { MeasureResult, NoteResult };
export type { ClipAxis };

export class Viewer3D {
  private renderer: THREE.WebGLRenderer;
  private labelRenderer: CSS2DRenderer;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private controls: OrbitControls;
  private cameraManager: CameraManager;
  private floorPlan: FloorPlan;
  private roomPlanLayer: RoomPlanLayer | null = null;
  private alignTool: AlignTool | null = null;
  private currentAlignment: AlignmentData | null = null;
  private clipPlanes: ClipPlanes;
  private annotations: AnnotationManager;
  private noteAnnotations: NoteAnnotationManager;
  private measureTool: MeasureTool;
  private noteTool: NoteTool;
  private animFrameId = 0;
  private lastTime = 0;
  private currentModel: THREE.Group | null = null;
  private modelSize = new THREE.Vector3();
  private ceilingY = 0;
  private meshes: THREE.Mesh[] = [];
  private axesHelper: THREE.AxesHelper | null = null;
  private floorPlanLines: THREE.Group | null = null;
  private planRoot: THREE.Group;
  private orientationDiv: HTMLElement;
  private streetView: StreetView | null = null;
  private pano360: Pano360View | null = null;
  private pano360Align: Pano360Align | null = null;
  /** Whether plan-mode auto ceiling clip is engaged. */
  private planCeilingActive = false;

  onMeasureComplete: ((r: MeasureResult) => void) | null = null;
  onNoteComplete: ((r: NoteResult) => void) | null = null;
  onPanoExit: (() => void) | null = null;
  /** Fires when a 360° panorama (equirect) session opens. */
  onPano360Enter: ((nodeId: number) => void) | null = null;

  constructor(private container: HTMLElement) {
    // WebGL renderer
    this.renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.shadowMap.enabled = true;
    this.renderer.localClippingEnabled = true;
    container.appendChild(this.renderer.domElement);

    // CSS2D overlay for measurement labels
    this.labelRenderer = new CSS2DRenderer();
    this.labelRenderer.domElement.style.cssText =
      "position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;overflow:hidden;";
    container.appendChild(this.labelRenderer.domElement);

    // Scene + lights
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x0f0f0f);
    this.planRoot = new THREE.Group();
    this.scene.add(this.planRoot);
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const sun = new THREE.DirectionalLight(0xffffff, 1.2);
    sun.position.set(10, 20, 10);
    sun.castShadow = true;
    this.scene.add(sun);

    // Camera + controls
    this.camera = new THREE.PerspectiveCamera(45, 1, 0.01, 2000);
    this.camera.position.set(0, 10, 12);
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.maxPolarAngle = Math.PI / 1.8;

    // Sub-systems
    this.cameraManager = new CameraManager(this.camera, this.controls);
    this.floorPlan = new FloorPlan(container);
    this.clipPlanes = new ClipPlanes(this.renderer, this.scene, this.camera, container);
    this.clipPlanes.onChange = () => this._syncClipPlanes();
    this.annotations = new AnnotationManager(this.scene);
    this.noteAnnotations = new NoteAnnotationManager(this.scene);
    this.measureTool = new MeasureTool(this.renderer.domElement, this.camera, this.annotations);
    this.noteTool = new NoteTool(this.renderer.domElement, this.camera, this.scene);

    this.measureTool.onComplete = (r) => {
      this.onMeasureComplete?.(r);
    };
    this.measureTool.onCursorChange = (active) => {
      // Disable orbit while measuring so clicks don't rotate the scene
      this.controls.enabled = !active;
    };
    this.noteTool.onComplete = (r) => {
      this.onNoteComplete?.(r);
    };
    this.noteTool.onCursorChange = (active) => {
      this.controls.enabled = !active;
    };

    // Pointer lock for walkthrough (disabled during 360° panorama)
    this.renderer.domElement.addEventListener("click", () => {
      if (this.cameraManager.getMode() === "walkthrough" && !this.measureTool["active"] && !this.streetView?.isPanoActive) {
        this.renderer.domElement.requestPointerLock();
      }
    });
    document.addEventListener("mousemove", (e) => {
      if (document.pointerLockElement === this.renderer.domElement) {
        this.cameraManager.applyPointerDelta(e.movementX, e.movementY);
      }
    });

    // Orientation debug overlay (only in debug mode)
    this.orientationDiv = document.createElement("div");
    this.orientationDiv.style.cssText = `
      position: absolute; top: 60px; left: 12px;
      background: rgba(0,0,0,0.7); color: #ccc; font: 12px monospace;
      padding: 8px 10px; border-radius: 6px; pointer-events: none;
      white-space: pre; z-index: 10;
      display: ${debugEnabled ? "block" : "none"};
    `;
    container.appendChild(this.orientationDiv);

    this._resize();
    new ResizeObserver(() => this._resize()).observe(container);
    this._startLoop();
  }

  async loadProject(meshUrl: string, floorPlanUrl?: string, meshFormat: "glb" | "ply" | "usdz" = "glb"): Promise<void> {
    if (this.currentModel) {
      this.currentModel.removeFromParent();
      disposeModel(this.currentModel);
      this.currentModel = null;
      this.meshes = [];
    }
    this.roomPlanLayer?.dispose();
    this.roomPlanLayer = null;
    this.alignTool?.dispose();
    this.alignTool = null;
    this.currentAlignment = null;
    this.planRoot.rotation.y = 0;
    this.annotations.clear();

    const model = await loadModel(meshUrl, meshFormat);
    this.modelSize = centerModel(model);
    this.planRoot.add(model);
    this.currentModel = model;
    this.meshes = collectMeshes(model);

    // Compute ceiling height for plan mode auto-clip
    const box = new THREE.Box3().setFromObject(model);
    this.ceilingY = detectCeilingY(model, box);

    const maxDim = Math.max(this.modelSize.x, this.modelSize.y, this.modelSize.z);
    this.camera.near = maxDim * 0.001;
    this.camera.far = maxDim * 20;
    this.camera.updateProjectionMatrix();
    this.cameraManager.setModelSize(this.modelSize);

    // After centerModel: X/Z centered at 0, Y goes from 0 (floor) to ~modelSize.y (top)
    this.clipPlanes.setModelBounds(this.modelSize, new THREE.Vector3(0, this.modelSize.y / 2, 0));

    // Axes helper (R=X, G=Y, B=Z)
    if (this.axesHelper) this.scene.remove(this.axesHelper);
    this.axesHelper = new THREE.AxesHelper(maxDim * 0.4);
    this.scene.add(this.axesHelper);

    // Orientation info
    const sx = this.modelSize.x, sy = this.modelSize.y, sz = this.modelSize.z;
    const minAxis = sx < sy && sx < sz ? "X" : sy < sz ? "Y" : "Z";
    const rot = model.rotation;
    this.orientationDiv.textContent =
      `format: ${meshFormat}\n` +
      `size: X=${sx.toFixed(2)} Y=${sy.toFixed(2)} Z=${sz.toFixed(2)}\n` +
      `smallest: ${minAxis} (likely up)\n` +
      `rotation: X=${THREE.MathUtils.radToDeg(rot.x).toFixed(1)}° ` +
      `Y=${THREE.MathUtils.radToDeg(rot.y).toFixed(1)}° ` +
      `Z=${THREE.MathUtils.radToDeg(rot.z).toFixed(1)}°`;

    await this.cameraManager.setMode("dollhouse");

    if (floorPlanUrl) {
      await this.floorPlan.load(floorPlanUrl, (nx, nz) => this._teleportFromFloorPlan(nx, nz));
      this._buildFloorPlanLines();
    }
  }

  async setMode(mode: CameraMode): Promise<void> {
    const targetRotation = mode === "floorplan" ? -this.floorPlan.rotation : 0;
    this._tweenPlanRotation(targetRotation);
    await this.cameraManager.setMode(mode);
    if (mode !== "floorplan") {
      // Hide 3D lines when leaving plan mode
      if (this.floorPlanLines) this.floorPlanLines.visible = false;
      // Disable plan ceiling clip when leaving plan mode
      this.clearSectionHeight();
    } else {
      // Entering plan mode — enable ceiling clip
      this._applyPlanCeiling();
    }
  }

  async loadFloorPlan(url: string): Promise<void> {
    await this.floorPlan.load(url, (nx, nz) => this._teleportFromFloorPlan(nx, nz));
    this._buildFloorPlanLines();
  }

  loadFloorPlanFromJson(data: IcRoomPlanJson): void {
    this.floorPlan.loadFromJson(data, (nx, nz) => this._teleportFromFloorPlan(nx, nz));
    this._buildFloorPlanLines();
  }

  get hasFloorPlan(): boolean {
    return this.floorPlan.loaded;
  }

  get floorPlanOverlayVisible(): boolean {
    return this.floorPlanLines?.visible ?? false;
  }

  get minimapVisible(): boolean {
    return this.floorPlan.visible;
  }

  setMinimapVisible(visible: boolean): void {
    if (visible) this.floorPlan.show();
    else this.floorPlan.hide();
  }

  toggleFloorPlan(): void {
    if (this.floorPlanLines) {
      this.floorPlanLines.visible = !this.floorPlanLines.visible;
    }
  }

  // ── RoomPlan parametric layer ───────────────────────────────────────────

  async loadRoomPlan(data: RoomPlanData, glbUrl?: string): Promise<void> {
    this.roomPlanLayer?.dispose();
    let glbScene: THREE.Group | null = null;
    if (glbUrl) {
      try {
        glbScene = await loadRoomPlanGlb(glbUrl);
      } catch (err) {
        console.warn("RoomPlan GLB load failed; falling back to parametric layer", err);
        glbScene = null;
      }
    }
    this.roomPlanLayer = new RoomPlanLayer(data, glbScene);
    this.roomPlanLayer.setAlignment(this.currentAlignment);
    this.planRoot.add(this.roomPlanLayer.group);
  }

  get hasRoomPlanLayer(): boolean {
    return this.roomPlanLayer !== null;
  }

  get roomPlanLayerVisible(): boolean {
    return this.roomPlanLayer?.visible ?? false;
  }

  toggleRoomPlanLayer(): void {
    if (!this.roomPlanLayer) return;
    this.roomPlanLayer.setVisible(!this.roomPlanLayer.visible);
  }

  // ── Alignment ───────────────────────────────────────────────────────────

  /** Store + apply a known alignment transform (e.g. loaded from backend). */
  setAlignmentTransform(data: AlignmentData | null): void {
    this.currentAlignment = data;
    this.roomPlanLayer?.setAlignment(data);
  }

  getAlignmentTransform(): AlignmentData | null {
    return this.currentAlignment;
  }

  /**
   * Start pair-picking alignment. The caller receives state callbacks
   * (current step + pair count) via `onState`. Use `finishAlignment()` to
   * commit or `cancelAlignment()` to discard.
   *
   * Requires the RoomPlan layer and the floor-plan minimap to be loaded.
   */
  startAlignment(
    onState: (step: AlignStep, count: number) => void,
  ): boolean {
    if (!this.roomPlanLayer || !this.floorPlan.loaded) return false;
    if (!this.alignTool) {
      this.alignTool = new AlignTool(
        this.renderer.domElement,
        this.camera,
        this.scene,
        this.floorPlan,
        () => this.meshes,
      );
    }
    this.alignTool.onStateChange = onState;
    this.alignTool.activate();
    // Ensure the minimap is visible so the user can click it.
    if (!this.floorPlan.visible) this.floorPlan.show();
    return true;
  }

  undoAlignmentPair(): void {
    this.alignTool?.removeLastPair();
  }

  resetAlignmentPairs(): void {
    this.alignTool?.reset();
  }

  /**
   * Compute, apply and return the alignment. Does NOT persist to backend —
   * caller owns that. Returns null if there are fewer than 2 pairs.
   */
  finishAlignment(): (AlignmentData & { rmse: number }) | null {
    if (!this.alignTool) return null;
    const result = this.alignTool.compute();
    this.alignTool.deactivate();
    if (!result) return null;

    const data: AlignmentData = {
      target: result.target,
      source: result.source,
      yaw: result.yaw,
      tx: result.tx,
      tz: result.tz,
      rmse_meters: result.rmse_meters,
      method: result.method,
    };
    this.setAlignmentTransform(data);
    return result;
  }

  cancelAlignment(): void {
    this.alignTool?.deactivate();
  }

  clearAlignment(): void {
    this.setAlignmentTransform(null);
  }

  getMode(): CameraMode {
    return this.cameraManager.getMode();
  }

  /** Start a measurement. Results come back via onMeasureComplete. */
  startMeasure(type: MeasurementType): void {
    this.noteTool.deactivate();
    this.measureTool.activate(type, this.meshes);
  }

  stopMeasure(): void {
    this.measureTool.deactivate();
  }

  /** Start note placement. Results come back via onNoteComplete. */
  startNote(category: NoteCategory): void {
    this.measureTool.deactivate();
    this.noteTool.activate(category, this.meshes);
  }

  stopNote(): void {
    this.noteTool.deactivate();
  }

  /** Add a completed measurement annotation to the scene. */
  addAnnotation(
    id: string,
    type: MeasurementType,
    points: THREE.Vector3[],
    value: number,
    unit: string,
    label?: string | null,
  ): void {
    this.annotations.addMeasurement(id, type, points, value, unit, label);
  }

  removeAnnotation(id: string): void {
    this.annotations.remove(id);
  }

  clearAnnotations(): void {
    this.annotations.clear();
  }

  /** Add a note annotation to the scene. Point must be in world space. */
  addNoteAnnotation(id: string, category: NoteCategory, point: THREE.Vector3, text: string): void {
    this.noteAnnotations.addNote(id, category, point, text);
  }

  removeNoteAnnotation(id: string): void {
    this.noteAnnotations.remove(id);
  }

  /** Toggle measurement annotations visibility. Returns new state. */
  setMeasurementsVisible(visible: boolean): void {
    this.annotations.setVisible(visible);
  }

  /** Toggle note annotations visibility. Returns new state. */
  setNotesVisible(visible: boolean): void {
    this.noteAnnotations.setVisible(visible);
  }

  // ── Streetview ──────────────────────────────────────────────────────────────

  /** Load streetview data (manifest + images) for the current project. */
  async loadStreetView(
    manifestUrl: string,
    imageBaseUrl: string,
    imageUrlQuery: string = "",
  ): Promise<void> {
    if (!this.currentModel) return;

    this.streetView = new StreetView(
      this.camera,
      this.renderer,
      this.scene,
      this.controls,
      this.planRoot,
      this.meshes,
    );

    this.streetView.onPanoExit = () => this.onPanoExit?.();

    await this.streetView.loadManifest(
      manifestUrl,
      imageBaseUrl,
      this.currentModel.position,
      imageUrlQuery,
    );
  }

  get hasStreetView(): boolean {
    return this.streetView?.loaded ?? false;
  }

  get isStreetViewActive(): boolean {
    return (this.streetView?.isActive || this.streetView?.isPanoActive) ?? false;
  }

  get isStreetViewPanoActive(): boolean {
    return this.streetView?.isPanoActive ?? false;
  }

  setStreetViewMarkersVisible(visible: boolean): void {
    this.streetView?.setMarkersVisible(visible);
  }

  enterStreetView(nodeId: number): void {
    this.streetView?.enter(nodeId);
  }

  exitStreetView(): void {
    this.streetView?.exit();
  }

  enterPanorama(): void {
    this.streetView?.enterPanorama();
  }

  // ── 360° panorama (Insta360) ────────────────────────────────────────────────

  async loadPano360(projectId: string, manifestUrl: string, imageBaseUrl: string): Promise<void> {
    if (!this.currentModel) return;

    this.pano360 = new Pano360View(
      this.camera,
      this.renderer,
      this.scene,
      this.controls,
      this.planRoot,
    );
    this.pano360.onPanoExit = () => {
      this.pano360Align?.notifyPanoExited();
      this.onPanoExit?.();
    };
    this.pano360.onPanoEnter = (nodeId) => {
      this.pano360Align?.notifyPanoEntered(nodeId);
      this.onPano360Enter?.(nodeId);
    };
    await this.pano360.loadManifest(manifestUrl, imageBaseUrl, this.currentModel.position);

    // Alignment controller — created lazily, lives as long as the view does.
    this.pano360Align = new Pano360Align(
      this.camera,
      this.renderer,
      this.controls,
      this.pano360,
      this.meshes,
      this.scene,
    );
    this.pano360Align.setProjectId(projectId);
  }

  setPano360AlignActive(on: boolean): void {
    if (!this.pano360Align || !this.pano360) return;
    this.pano360Align.setActive(on);
    this.pano360.setInteractive(!on);
  }

  get isPano360AlignActive(): boolean {
    return this.pano360Align?.isActive ?? false;
  }

  get hasPano360(): boolean {
    return this.pano360?.loaded ?? false;
  }

  get isPano360Active(): boolean {
    return this.pano360?.isPanoActive ?? false;
  }

  get pano360MarkersVisible(): boolean {
    return this.pano360?.markersVisible ?? false;
  }

  setPano360MarkersVisible(visible: boolean): void {
    this.pano360?.setMarkersVisible(visible);
  }

  enterPano360(): void {
    this.pano360?.enterPanorama();
  }

  exitPano360(): void {
    this.pano360?.exitPanorama();
  }

  // ── Clipping planes ─────────────────────────────────────────────────────────

  setClipModeActive(on: boolean): void {
    this.clipPlanes.setActive(on);
    if (!on) this._syncClipPlanes();
  }

  get isClipModeActive(): boolean {
    return this.clipPlanes.isActive;
  }

  toggleClipAxis(axis: ClipAxis): void {
    this.clipPlanes.toggleAxis(axis);
  }

  getClipAxisState(axis: ClipAxis): { enabled: boolean; offset: number } {
    return this.clipPlanes.getAxisState(axis);
  }

  setClipAxisOffset(axis: ClipAxis, offset: number): number {
    return this.clipPlanes.setAxisOffset(axis, offset);
  }

  getClipModelSize(): THREE.Vector3 {
    return this.clipPlanes.getModelSize();
  }

  /** Get detected ceiling Y for plan mode. */
  getCeilingY(): number {
    return this.ceilingY;
  }

  /** Set section height percent (0–100). 100 = full height (no clip), 0 = floor. */
  setSectionHeight(pct: number): void {
    const maxY = this.modelSize.y;
    const clipY = maxY * (pct / 100);
    this.clipPlanes.enableAxis("y", clipY - this.modelSize.y * 0.5);
    this.planCeilingActive = true;
    this._syncClipPlanes();
  }

  /** Disable the Y-axis section clip. */
  clearSectionHeight(): void {
    if (!this.planCeilingActive) return;
    this.planCeilingActive = false;
    this.clipPlanes.disableAxis("y");
  }

  /** Highlight a measurement annotation: focus camera on it + visual pulse. */
  highlightMeasurement(id: string): void {
    this.annotations.highlight(id);
    const pos = this.annotations.getWorldPosition(id);
    if (!pos) return;
    this._animateCameraTarget(pos);
  }

  /** Highlight a note annotation: focus camera on it + visual pulse. */
  highlightNote(id: string): void {
    this.noteAnnotations.highlight(id);
    const pos = this.noteAnnotations.getWorldPosition(id);
    if (!pos) return;
    // Smoothly orbit camera to look at the note
    this._animateCameraTarget(pos);
  }

  /** Smoothly move the orbit target to a new position. */
  private _animateCameraTarget(target: THREE.Vector3, ms = 500): void {
    const start = this.controls.target.clone();
    const t0 = performance.now();
    const tick = () => {
      const raw = Math.min((performance.now() - t0) / ms, 1);
      const t = raw < 0.5 ? 2 * raw * raw : -1 + (4 - 2 * raw) * raw;
      this.controls.target.lerpVectors(start, target, t);
      if (raw < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  takeScreenshot(): string {
    // Render one frame to ensure canvas is current
    this.renderer.render(this.scene, this.camera);
    return this.renderer.domElement.toDataURL("image/jpeg", 0.92);
  }

  dispose(): void {
    cancelAnimationFrame(this.animFrameId);
    this.alignTool?.dispose();
    this.alignTool = null;
    this.roomPlanLayer?.dispose();
    this.roomPlanLayer = null;
    this.streetView?.dispose();
    this.pano360Align?.dispose();
    this.pano360?.dispose();
    this.clipPlanes.dispose();
    this.measureTool.dispose();
    this.noteTool.dispose();
    this.cameraManager.dispose();
    this.floorPlan.dispose();
    this.annotations.dispose();
    this.noteAnnotations.dispose();
    this.controls.dispose();
    if (this.axesHelper) this.scene.remove(this.axesHelper);
    if (this.floorPlanLines) this.floorPlanLines.removeFromParent();
    if (this.currentModel) {
      this.currentModel.removeFromParent();
      disposeModel(this.currentModel);
    }
    this.scene.remove(this.planRoot);
    this.renderer.dispose();
    this.renderer.domElement.remove();
    this.labelRenderer.domElement.remove();
    this.orientationDiv.remove();
  }

  /** Push active clip planes to the renderer. */
  private _syncClipPlanes(): void {
    this.renderer.clippingPlanes = this.clipPlanes.getActivePlanes();
  }

  /** Apply ceiling clip for plan mode (default 85%). */
  private _applyPlanCeiling(): void {
    this.setSectionHeight(85);
  }

  private _startLoop(): void {
    const tick = (time: number) => {
      this.animFrameId = requestAnimationFrame(tick);
      const delta = Math.min((time - this.lastTime) / 1000, 0.1);
      this.lastTime = time;

      // In any panorama mode the session owns the camera — skip camera/controls updates
      if (!this.streetView?.isPanoActive && !this.pano360?.isPanoActive) {
        this.cameraManager.update(delta);
        if (this.cameraManager.getMode() !== "walkthrough") {
          this.controls.update();
        }
        // Sync floor plan minimap rotation with planRoot.
        this.floorPlan.setRotation(-this.planRoot.rotation.y);

        // Sync camera indicator on the minimap
        if (this.modelSize.x > 0 && this.modelSize.z > 0) {
          const rotY = this.planRoot.rotation.y;
          const cosR = Math.cos(rotY);
          const sinR = Math.sin(rotY);
          const lx = this.camera.position.x * cosR - this.camera.position.z * sinR;
          const lz = this.camera.position.x * sinR + this.camera.position.z * cosR;
          const nx = lx / this.modelSize.x + 0.5;
          const nz = lz / this.modelSize.z + 0.5;
          const dir = new THREE.Vector3();
          this.camera.getWorldDirection(dir);
          const dx = dir.x * cosR - dir.z * sinR;
          const dz = dir.x * sinR + dir.z * cosR;
          const yawLocal = Math.atan2(-dx, -dz);
          this.floorPlan.setCameraIndicator(nx, nz, -yawLocal);
        }
      }

      this.renderer.render(this.scene, this.camera);
      this.labelRenderer.render(this.scene, this.camera);
    };
    requestAnimationFrame(tick);
  }

  private _resize(): void {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    this.renderer.setSize(w, h, false);
    this.labelRenderer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    // Update Line2 material resolution for correct line width
    if (this.floorPlanLines) {
      this.floorPlanLines.traverse((obj) => {
        if (obj instanceof Line2) {
          (obj.material as LineMaterial).resolution.set(w, h);
        }
      });
    }
  }

  /**
   * Build Three.js line geometry from the parsed floor plan data.
   * RTABmap meshes (both PLY and GLB) are Z-up, rotated to Y-up on load:
   * mesh (x,y,z) → Three.js (x, z, -y).
   * Floor plan 2D coords are in the mesh's XY plane (up axis Z dropped).
   * model.position holds the centering offset applied by centerModel().
   */
  private _buildFloorPlanLines(): void {
    if (this.floorPlanLines) {
      this.floorPlanLines.removeFromParent();
      this.floorPlanLines = null;
    }

    const fpLines = this.floorPlan.lines;
    if (fpLines.length === 0 || !this.currentModel) return;

    const group = new THREE.Group();
    const material = new LineMaterial({
      color: 0xffffff,
      linewidth: 6, // pixels (screen-space, works on all platforms)
      depthTest: false,
      worldUnits: false,
    });
    material.resolution.set(this.renderer.domElement.clientWidth, this.renderer.domElement.clientHeight);

    // Centering offset (set by centerModel)
    const offX = this.currentModel.position.x;
    const offZ = this.currentModel.position.z;
    // Floor plan lines rendered slightly above floor (Y=0)
    const lineY = 0.05;

    for (const line of fpLines) {
      const positions: number[] = [];
      for (const pt of line.points) {
        // Plan 2D (x, y=world Z) → Three.js (x, lineY, z) + centering.
        // No sign flip: SVG is now authored without a Y mirror.
        positions.push(pt.x + offX, lineY, pt.y + offZ);
      }
      const geometry = new LineGeometry();
      geometry.setPositions(positions);
      group.add(new Line2(geometry, material));
    }

    group.visible = false; // hidden by default, toggled by button
    group.renderOrder = 999;
    this.planRoot.add(group);
    this.floorPlanLines = group;
  }

  /** Smoothly animate planRoot rotation (runs in parallel with camera transition). */
  private _tweenPlanRotation(target: number, ms = 600): void {
    const start = this.planRoot.rotation.y;
    if (Math.abs(target - start) < 1e-4) return;
    const t0 = performance.now();
    const tick = () => {
      const raw = Math.min((performance.now() - t0) / ms, 1);
      const t = raw < 0.5 ? 2 * raw * raw : -1 + (4 - 2 * raw) * raw;
      this.planRoot.rotation.y = start + (target - start) * t;
      if (raw < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  private _teleportFromFloorPlan(nx: number, nz: number): void {
    const x = (nx - 0.5) * this.modelSize.x;
    const z = (nz - 0.5) * this.modelSize.z;
    this.camera.position.set(x, 1.65, z);
    this.cameraManager.setMode("walkthrough");
  }
}
